import React, { ReactElement, Ref, useCallback } from 'react';

import PropTypes from 'prop-types';

import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import ToggleButton from './ToggleButton';
import { stripUnknownProps } from '../../helpers/internalHelpers';
import { GetToggleButtonProps, GenericItem, DataAttributes } from '../../types';

type ControlContainerProps<TItem extends GenericItem> =
  StylableComponent<HTMLDivElement> &
    DataAttributes & {
      getToggleButtonProps: GetToggleButtonProps;
      disabled?: boolean;
      isLoading?: boolean;
      error?: boolean;
      isOpen: boolean;
      isAutocomplete?: boolean;
      highDensity?: boolean;
      highlightButtonOnFocus?: boolean;
      temp?: TItem;
    };

const ControlContainerWrapped = <TItem extends GenericItem>(
  {
    className,
    style,
    disabled = false,
    isLoading = false,
    error,
    isOpen = false,
    getToggleButtonProps,
    isAutocomplete,
    children,
    highDensity,
    highlightButtonOnFocus,
    onClick,
    ...props
  }: ControlContainerProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  // if this is not autocomplete, the ref gets provided to getToggleButtonProps to merge them
  const {
    ref: divRef,
    onClick: divOnClick,
    ...divProps
  } = isAutocomplete
    ? { ref }
    : getToggleButtonProps({
        // TODO: remove this cast once this is merged https://github.com/downshift-js/downshift/pull/1568
        ref: ref as Ref<HTMLButtonElement>,
        disabled,
      });

  const controlLabelProps = stripUnknownProps(props, [
    'selectItem',
    'selectedItem',
    'itemToString',
    'openMenu',
  ]);

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        onClick?.(event);
        divOnClick?.(event);
      }
    },
    [disabled, divOnClick, onClick],
  );

  return (
    // This will be fixed in https://jira.autodesk.com/browse/DSGN-1832
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={divRef as Ref<HTMLDivElement>}
      data-testid={'Dropdown__control'}
      aria-disabled={disabled}
      className={className}
      css={[
        {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `1px ${theme.spacing.S}px`,
          border: `1px solid ${theme.tokens.colors.border.value}`,
          borderRadius: theme.borderRadius.small,
          cursor: 'pointer',
          outline: 'none',
          ...getInteractiveStyles(disabled),

          ...(isOpen && {
            borderColor: theme.tokens.colors.border.focused.value,
            boxShadow: theme.halos.focus,

            '&:hover': {
              boxShadow: theme.halos.focus,
            },
          }),

          ...(disabled && {
            backgroundColor:
              theme.tokens.colors.background.neutral.inactive.value,
            borderColor: theme.tokens.colors.border.inactive.value,
            color: theme.tokens.colors.text.inactive.value,
            cursor: 'not-allowed',
          }),

          ...(error &&
            !disabled && {
              borderColor: theme.tokens.colors.border.error.value,
            }),
        },
        style,
      ]}
      onClick={onClickHandler}
      {...divProps}
      {...controlLabelProps}
    >
      {children}
      {isLoading && (
        <ProgressRing
          size={ProgressRing.SIZES.XSMALL}
          style={{
            marginLeft: highDensity ? 0 : theme.spacing.XS,
          }}
        />
      )}
      {isAutocomplete ? (
        <ToggleButton
          isOpen={isOpen}
          disabled={disabled}
          getToggleButtonProps={getToggleButtonProps}
          tabIndex={-1}
          style={{
            marginLeft: highDensity ? 0 : theme.spacing.XS,
            marginRight: theme.spacing.XXS,
            ...(disabled && { color: theme.tokens.colors.icon.inactive.value }),
          }}
          highlightButtonOnFocus={highlightButtonOnFocus}
        />
      ) : (
        <div
          style={{
            height: 32,
            maxWidth: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid={'Control__toggle'}
        >
          <ChevronDownIcon
            style={[
              {
                maxHeight: 32,
                maxWidth: 36,
                color: theme.tokens.colors.icon.primary.value,
                marginLeft: highDensity ? 0 : theme.spacing.XS,
                marginRight: theme.spacing.XXS,
                ...(isOpen && {
                  transform: 'rotate(180deg)',
                }),
                ...(disabled && {
                  color: theme.tokens.colors.icon.inactive.value,
                }),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

const ControlContainer = React.forwardRef(ControlContainerWrapped);

ControlContainer.displayName = 'ControlContainer';

ControlContainer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** The content of te ControlContainer */
  children: PropTypes.any,
};

export default Object.assign(
  ControlContainer as <TItem extends GenericItem>(
    p: ControlContainerProps<TItem> & {
      ref?: Ref<HTMLDivElement>;
    },
  ) => ReactElement,
  { propTypes: ControlContainer.propTypes },
);
